import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Phone, Tag, DollarSign, Calendar, ChevronRight, X, Edit2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomersTab = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingTags, setEditingTags] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/api/admin/customers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.items || []);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const viewCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(`${API}/api/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const addTag = async () => {
    if (!newTag.trim() || !selectedCustomer) return;
    
    const updatedTags = [...(selectedCustomer.tags || []), newTag.trim()];
    
    try {
      await axios.put(`${API}/api/admin/customers/${selectedCustomer.customer_id}`, 
        { tags: updatedTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCustomer(prev => ({ ...prev, tags: updatedTags }));
      setNewTag('');
      toast.success('Tag agregado');
    } catch (error) {
      toast.error('Error al agregar tag');
    }
  };

  const removeTag = async (tagToRemove) => {
    if (!selectedCustomer) return;
    
    const updatedTags = selectedCustomer.tags.filter(t => t !== tagToRemove);
    
    try {
      await axios.put(`${API}/api/admin/customers/${selectedCustomer.customer_id}`, 
        { tags: updatedTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCustomer(prev => ({ ...prev, tags: updatedTags }));
      toast.success('Tag eliminado');
    } catch (error) {
      toast.error('Error al eliminar tag');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center">
          <Users className="w-6 h-6 mr-2 text-amber-500" />
          Clientes
        </h2>
        <p className="text-gray-400 text-sm">Base de datos de compradores</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por nombre, email o WhatsApp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-800 text-white pl-10 pr-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
        >
          Buscar
        </button>
      </form>

      {/* Customers List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl">
          <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-gray-400">No hay clientes registrados</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {customers.map((customer) => (
            <div 
              key={customer.customer_id}
              onClick={() => viewCustomerDetails(customer.customer_id)}
              className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 cursor-pointer hover:border-amber-500/50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-white">{customer.name}</h3>
                    {customer.tags?.length > 0 && (
                      <div className="flex gap-1">
                        {customer.tags.slice(0, 2).map((tag, i) => (
                          <span key={i} className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {customer.tags.length > 2 && (
                          <span className="text-xs text-gray-500">+{customer.tags.length - 2}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <span className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      {customer.email}
                    </span>
                    <span className="flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      {customer.whatsapp}
                    </span>
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-green-500" />
                      ${customer.total_spent?.toLocaleString()} ({customer.total_orders} órdenes)
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Customer Details Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCustomer(null)}>
          <div className="bg-zinc-900 rounded-2xl max-w-3xl w-full border border-zinc-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="text-xl font-bold text-white">{selectedCustomer.name}</h3>
              <button onClick={() => setSelectedCustomer(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-zinc-800 rounded-lg p-4">
                  <Mail className="w-5 h-5 text-amber-500 mb-2" />
                  <p className="text-gray-400 text-sm">Email</p>
                  <p className="text-white">{selectedCustomer.email}</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <Phone className="w-5 h-5 text-amber-500 mb-2" />
                  <p className="text-gray-400 text-sm">WhatsApp</p>
                  <p className="text-white">{selectedCustomer.whatsapp}</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <DollarSign className="w-5 h-5 text-green-500 mb-2" />
                  <p className="text-gray-400 text-sm">Total gastado</p>
                  <p className="text-white font-bold">${selectedCustomer.total_spent?.toLocaleString()}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{selectedCustomer.total_orders}</p>
                  <p className="text-gray-400 text-sm">Órdenes</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{selectedCustomer.events_attended?.length || 0}</p>
                  <p className="text-gray-400 text-sm">Eventos</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-sm text-white">{formatDate(selectedCustomer.first_purchase)}</p>
                  <p className="text-gray-400 text-sm">Primera compra</p>
                </div>
              </div>

              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center">
                    <Tag className="w-4 h-4 mr-2 text-amber-500" />
                    Tags
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedCustomer.tags?.map((tag, i) => (
                    <span 
                      key={i} 
                      className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm flex items-center group"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-2 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Agregar tag..."
                    className="flex-1 bg-zinc-800 text-white px-3 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 text-sm"
                  >
                    Agregar
                  </button>
                </div>
              </div>

              {/* Order History */}
              {selectedCustomer.orders?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                    Historial de compras
                  </h4>
                  <div className="space-y-2">
                    {selectedCustomer.orders.map((order) => (
                      <div key={order.order_id} className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white">{order.event_name}</p>
                          <p className="text-gray-500 text-sm">{order.access_level_name} x {order.num_attendees}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 font-medium">${order.total_amount?.toLocaleString()}</p>
                          <p className="text-gray-500 text-sm">{formatDate(order.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersTab;
